import { useLocation, useOutlet } from "react-router-dom";
import { useState, useEffect, ReactNode } from "react";
import { cloneDeep } from "lodash";

interface PersistentLayoutCachedPage {
  path: string;
  exact?: boolean;
  component: ReactNode;
}

interface PersistentLayoutCachedPageStatus {
  isLoaded?: boolean;
  isActive?: boolean;
}

interface PersistentLayoutCachedPageStatusCollection {
  [path: string]: PersistentLayoutCachedPageStatus | undefined;
}

interface PersistentLayoutProps {
  cachedPages: PersistentLayoutCachedPage[];
  children?: ReactNode;
}

const matchPath = (
  currentPath: string,
  targetPath: string,
  exact?: boolean
): boolean => {
  if (exact) return currentPath === targetPath;
  return currentPath.startsWith(targetPath);
};

const PersistentLayout: React.FC<PersistentLayoutProps> = ({
  cachedPages,
  children,
}) => {
  const location = useLocation();
  const outlet = useOutlet();
  const [cachedPageStatusCollection, setCachedPageStatusCollection] =
    useState<PersistentLayoutCachedPageStatusCollection>({});
  const currentPath = location.pathname;

  useEffect(() => {
    const updatedCachedPageStatusCollection: PersistentLayoutCachedPageStatusCollection =
      cloneDeep(cachedPageStatusCollection);
    cachedPages.forEach(({ path, exact }) => {
      const isMatch = matchPath(currentPath, path, exact);
      let isLoaded = false;
      let isActive = false;
      if (isMatch) {
        isLoaded = true;
        isActive = true;
      } else if (updatedCachedPageStatusCollection?.[path]) {
        isLoaded = updatedCachedPageStatusCollection?.[path]?.isLoaded ?? false;
        isActive = false;
      }
      updatedCachedPageStatusCollection[path] = {
        isLoaded,
        isActive,
      };
    });
    setCachedPageStatusCollection(updatedCachedPageStatusCollection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, cachedPages]);

  return (
    <>
      {cachedPages.map(({ path, component }) => {
        const cachedPageStatusCollectionItem =
          cachedPageStatusCollection?.[path];
        const isLoaded = cachedPageStatusCollectionItem?.isLoaded ?? false;
        const isActive = cachedPageStatusCollectionItem?.isActive ?? false;
        return isLoaded ? (
          <div
            key={path}
            className="cached-page"
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              visibility: isActive ? "visible" : "hidden",
            }}
          >
            {component}
          </div>
        ) : null;
      })}
      {children || outlet}
    </>
  );
};

export default PersistentLayout;
